import React from "react";
import "./styles/closingProcessPanel.scss";
import { formatMoney } from "../../utils/dataExtractionUtils";
import { stateBanner, toStepView, toResultView } from "../../utils/cierreClosingDetail";

// The cuadre process view — status, steps and results (comparisons) for one cierre closing.
// Pure rendering over `getCierreClosingDetail`'s response; `utils/cierreClosingDetail.js` does
// every judgement call (CUADRE EXITOSO, state label, step/result shaping) so this stays a
// template. Deliberately does not render alerts — the existing `DocumentAnalysisModule`
// `type="alerts"` pane (BR-31) already does, from the same closing id, and stays as-is.
function ClosingProcessPanel({ detail, loading }) {
  if (loading) {
    return (
      <div className="cierre-process-panel cierre-process-panel-loading">
        <span>Cargando el proceso del cierre...</span>
      </div>
    );
  }

  if (!detail) {
    return null;
  }

  const banner = stateBanner(detail);
  const steps = (detail.sources || []).map(toStepView);
  const results = (detail.calculations || []).map(toResultView);

  return (
    <div className="cierre-process-panel">
      <div className={`cierre-process-banner cierre-process-banner-${banner.tone}`}>
        <i
          className={`fas ${
            banner.tone === "ok"
              ? "fa-check-circle"
              : banner.tone === "warn"
              ? "fa-exclamation-triangle"
              : "fa-hourglass-half"
          }`}
        ></i>
        <span>{banner.label}</span>
      </div>

      <div className="cierre-process-columns">
        <div className="cierre-process-column">
          <h4 className="cierre-process-column-title">Pasos</h4>
          {steps.length === 0 && (
            <p className="cierre-process-empty">Aún no hay fuentes registradas.</p>
          )}
          <ul className="cierre-process-steps">
            {steps.map((step) => (
              <li
                key={step.key}
                className={`cierre-process-step ${
                  step.ok ? "cierre-process-step-ok" : "cierre-process-step-pending"
                }`}
              >
                <i className={`fas ${step.ok ? "fa-check" : "fa-clock"}`}></i>
                <div className="cierre-process-step-body">
                  <span className="cierre-process-step-label">{step.label}</span>
                  <span className="cierre-process-step-meta">
                    {step.fileCount} documento{step.fileCount === 1 ? "" : "s"} ·{" "}
                    {step.datapointCount} dato{step.datapointCount === 1 ? "" : "s"}
                    {step.status ? ` · ${step.status}` : ""}
                  </span>
                  {step.error && (
                    <span className="cierre-process-step-error">{step.error}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="cierre-process-column">
          <h4 className="cierre-process-column-title">Resultados</h4>
          {results.length === 0 && (
            <p className="cierre-process-empty">Aún no hay cálculos registrados.</p>
          )}
          <table className="cierre-process-results-table">
            <tbody>
              {results.map((result) => (
                <tr
                  key={result.key}
                  className={
                    result.resolved
                      ? "cierre-process-result-resolved"
                      : "cierre-process-result-unresolved"
                  }
                >
                  <td className="cierre-process-result-key">
                    {result.key}
                    {result.changed && (
                      <span className="cierre-process-result-changed" title="Cambió en la última evaluación">
                        ●
                      </span>
                    )}
                  </td>
                  <td className="cierre-process-result-value">
                    {result.resolved ? `$${formatMoney(result.value)}` : "Sin resolver"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ClosingProcessPanel;
